import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader } from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { ASSISTENT_API_URL } from '@/config/backend'
import { useAuthStore } from '@/stores/auth-store'
import { useDevicesStore } from '@/stores/devices-store'
import { Call } from '@/types/calls'
import axios from 'axios'
import { format } from 'date-fns'
import { de } from 'date-fns/locale'
import {
  Bookmark,
  Calendar,
  ChevronLeft,
  ChevronRight,
  Clock,
  Copy,
  Edit,
  Loader2,
  Mail,
  MoreVertical,
  Phone,
  PhoneForwarded,
  Triangle,
  Users,
  X
} from 'lucide-react'
import { useEffect, useState } from 'react'

interface CallDetailsModalProps {
  call: Call | null
  open: boolean
  onClose: () => void
  onNavigate?: (direction: 'prev' | 'next') => void
  canNavigatePrev?: boolean
  canNavigateNext?: boolean
}

interface Message {
  id: string
  sender: 'ai' | 'caller'
  text: string
  timestamp: Date
  editable?: boolean
}

export function CallDetailsModal({
  call,
  open,
  onClose,
  onNavigate,
  canNavigatePrev = false,
  canNavigateNext = false
}: CallDetailsModalProps) {
  const [copied, setCopied] = useState(false)
  const [messages, setMessages] = useState<Message[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { getApiHeaders } = useAuthStore()
  const { currentDeviceSerialId } = useDevicesStore()

  // Load conversation history when call changes
  useEffect(() => {
    if (!call || !open) return

    const loadConversationHistory = async () => {
      setLoading(true)
      setError(null)

      console.log('========================================')
      console.log('🔍 LOADING CONVERSATION DATA')
      console.log('Session ID:', call.sessionId)
      console.log('========================================')

      try {
        // First, try to get conversation data from the call object itself
        // The conversation data is stored in actions[0].data
        const conversationData = call.actions[0]?.data

        console.log('📦 CALL OBJECT INSPECTION:')
        console.log('- Has actions:', !!call.actions)
        console.log('- Actions length:', call.actions?.length)
        console.log('- First action type:', call.actions?.[0]?.type)
        console.log('- First action data exists:', !!conversationData)

        if (conversationData) {
          console.log('📋 CONVERSATION DATA KEYS:', Object.keys(conversationData))
          console.log('📋 FULL CONVERSATION DATA:', JSON.stringify(conversationData, null, 2))
        }

        if (conversationData && (conversationData.transcript || conversationData.messages || conversationData.conversationElements)) {
          console.log('✅ Found conversation data in call object!')

          // Use data from call object
          const transcript = conversationData.transcript || conversationData.messages || conversationData.conversationElements || []
          console.log('📝 Transcript array length:', transcript.length)
          console.log('📝 First message:', transcript[0])

          const transformedMessages: Message[] = transcript.map((msg: any, index: number) => ({
            id: msg.id || `msg-${index}`,
            sender: msg.sender === 'ASSISTANT' || msg.type === 'AI' || msg.role === 'assistant' ? 'ai' : 'caller',
            text: msg.text || msg.message || msg.content || '',
            timestamp: msg.timestamp ? new Date(msg.timestamp) : new Date(call.startTime),
            editable: msg.editable || (msg.sender === 'ASSISTANT' && msg.source === 'KNOWLEDGE_BASE')
          }))

          console.log('✅ Transformed messages:', transformedMessages.length)
          setMessages(transformedMessages)
          setLoading(false)
          return
        }

        console.log('⚠️ No conversation data in call object, trying API...')

        // Try multiple possible API endpoints
        const possibleEndpoints = [
          `${ASSISTENT_API_URL}device/${currentDeviceSerialId}/conversation/${call.sessionId}`,
          `${ASSISTENT_API_URL}device/${currentDeviceSerialId}/session/${call.sessionId}`,
          `${ASSISTENT_API_URL}conversation/${call.sessionId}`,
          `${ASSISTENT_API_URL}session/${call.sessionId}`
        ]

        let response = null
        let successfulEndpoint = null

        for (const apiUrl of possibleEndpoints) {
          try {
            console.log('🌐 Trying API URL:', apiUrl)

            response = await axios.get(apiUrl, {
              headers: getApiHeaders()
            })

            if (response.status === 200 && response.data.errorCode === null) {
              successfulEndpoint = apiUrl
              console.log('✅ SUCCESS with endpoint:', apiUrl)
              break
            }
          } catch (err: any) {
            console.log('❌ Failed with endpoint:', apiUrl, '- Status:', err.response?.status)
            continue
          }
        }

        if (!response || !successfulEndpoint) {
          throw new Error('No valid API endpoint found for conversation data')
        }

        console.log('📡 API RESPONSE:')
        console.log('- Successful endpoint:', successfulEndpoint)
        console.log('- Status:', response.status)
        console.log('- Error Code:', response.data.errorCode)
        console.log('- Has resultObject:', !!response.data.resultObject)
        console.log('- Full response:', JSON.stringify(response.data, null, 2))

        const apiData = response.data.resultObject || {}
        console.log('📋 API DATA KEYS:', Object.keys(apiData))
        console.log('📋 API DATA:', JSON.stringify(apiData, null, 2))

        const transformedMessages: Message[] = []

        // Add greeting messages first (AI)
        if (apiData.greeting && Array.isArray(apiData.greeting)) {
          console.log('📝 Processing greeting:', apiData.greeting.length)
          apiData.greeting.forEach((greeting: any, index: number) => {
            transformedMessages.push({
              id: `greeting-${index}`,
              sender: 'ai',
              text: greeting.text || '',
              timestamp: new Date(call.startTime),
              editable: greeting.type === 'GREETING'
            })
          })
        }

        // Process utterances (conversation turns)
        if (apiData.utterances && Array.isArray(apiData.utterances)) {
          console.log('📝 Processing utterances:', apiData.utterances.length)

          apiData.utterances.forEach((utterance: any, utteranceIndex: number) => {
            // Add caller message (user utterance)
            if (utterance.utterance) {
              transformedMessages.push({
                id: `caller-${utteranceIndex}`,
                sender: 'caller',
                text: utterance.utterance,
                timestamp: utterance.utteranceTime ? new Date(utterance.utteranceTime) : new Date(call.startTime),
                editable: false
              })
            }

            // Add AI answer messages
            if (utterance.answers && Array.isArray(utterance.answers)) {
              utterance.answers.forEach((answer: any, answerIndex: number) => {
                if (answer.text) {
                  transformedMessages.push({
                    id: `ai-${utteranceIndex}-${answerIndex}`,
                    sender: 'ai',
                    text: answer.text,
                    timestamp: utterance.utteranceTime ? new Date(utterance.utteranceTime) : new Date(call.startTime),
                    editable: answer.type === 'ANSWER' || answer.type === 'FOLLOWUP'
                  })
                }
              })
            }
          })
        }

        console.log('✅ Transformed messages from API:', transformedMessages.length)
        console.log('📝 Sample messages:', transformedMessages.slice(0, 3))
        setMessages(transformedMessages)
      } catch (err: any) {
        console.error('❌ FAILED TO LOAD CONVERSATION:', err)
        console.error('Error details:', {
          message: err.message,
          response: err.response?.data,
          status: err.response?.status
        })

        setError('Conversation data not available')
        setMessages([])
      } finally {
        setLoading(false)
        console.log('========================================')
      }
    }

    loadConversationHistory()
  }, [call, open, getApiHeaders, currentDeviceSerialId])

  if (!call) return null

  // Format call time
  const startTime = new Date(call.startTime)
  const endTime = new Date(call.endTime)
  const dateStr = format(startTime, 'dd.MM.yyyy', { locale: de })
  const timeRange = `${format(startTime, 'HH:mm', { locale: de })} to ${format(endTime, 'HH:mm', { locale: de })}`

  // Copy session ID to clipboard
  const handleCopySessionId = async () => {
    try {
      await navigator.clipboard.writeText(call.sessionId)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy session ID:', err)
      // Could show error toast here
    }
  }

  // Report issue via email
  const handleReportIssue = () => {
    const subject = encodeURIComponent(`Issue Report - Session ${call.sessionId}`)
    const body = encodeURIComponent(`Session ID: ${call.sessionId}\n\nPlease describe the issue:`)
    window.location.href = `mailto:support@assistent.ai?subject=${subject}&body=${body}`
  }

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose()
    } else if (e.key === 'ArrowLeft' && canNavigatePrev && onNavigate) {
      onNavigate('prev')
    } else if (e.key === 'ArrowRight' && canNavigateNext && onNavigate) {
      onNavigate('next')
    }
  }

  // Determine if call was forwarded
  const forwardedProfile = call.actions.find(a =>
    a.type.includes('FORWARD') || a.type.includes('CALLFORWARD')
  )?.data?.forwardProfileName || 'Service team'

  // Check if SMS was sent
  const smsSent = call.actions.some(a =>
    a.type.includes('SMS') || a.type.includes('MESSAGE')
  )

  // Determine booking outcome
  const hasBooking = call.actions.some(a =>
    a.type === 'BOOKING_DONE' || a.type === 'BUSINESSDATA_DONE'
  )

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        className="max-w-3xl h-[90vh] p-0 gap-0 flex flex-col overflow-hidden"
        onKeyDown={handleKeyDown}
        onInteractOutside={onClose}
      >
        {/* Sticky Header with Call Metadata */}
        <div className="sticky top-0 z-10 bg-white border-b border-gray-200">
          {/* Top navigation bar */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
            <DialogHeader className="flex-row items-center gap-3 space-y-0">
              <h2 className="text-lg font-semibold text-gray-900">
                {dateStr} {timeRange}
              </h2>
            </DialogHeader>

            <div className="flex items-center gap-2">
              {/* Navigation arrows */}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onNavigate?.('prev')}
                disabled={!canNavigatePrev}
                className="h-8 w-8 p-0"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onNavigate?.('next')}
                disabled={!canNavigateNext}
                className="h-8 w-8 p-0"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>

              {/* Bookmark/Review toggle */}
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0"
              >
                <Bookmark className="h-4 w-4" />
              </Button>

              {/* Close button */}
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                className="h-8 w-8 p-0"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Call metadata */}
          <div className="px-6 py-4 space-y-4">
            {/* Caller info */}
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 text-gray-700">
                <Phone className="h-4 w-4" />
                <span className="font-medium">{call.callerPhone || 'Unknown'}</span>
              </div>
              <span className="text-gray-900 font-medium">{call.callerName || 'N. A.'}</span>
            </div>

            {/* Booking details */}
            {(call.bookingTime || call.guestCount || call.bookingId) && (
              <div className="flex items-center gap-6 text-sm">
                {call.bookingTime && (
                  <div className="flex items-center gap-2 text-gray-700">
                    <Calendar className="h-4 w-4" />
                    <span>{format(new Date(call.bookingTime), 'dd.MM.yyyy', { locale: de })}</span>
                  </div>
                )}
                {call.bookingTime && (
                  <div className="flex items-center gap-2 text-gray-700">
                    <Clock className="h-4 w-4" />
                    <span>{format(new Date(call.bookingTime), 'HH:mm', { locale: de })}</span>
                  </div>
                )}
                {call.guestCount && (
                  <div className="flex items-center gap-2 text-gray-700">
                    <Users className="h-4 w-4" />
                    <span>{call.guestCount}</span>
                  </div>
                )}
                {call.bookingId && (
                  <div className="flex items-center gap-2 text-gray-700">
                    <Triangle className="h-4 w-4 fill-purple-500 text-purple-500" />
                    <span className="font-mono">{call.bookingId}</span>
                  </div>
                )}
              </div>
            )}

            {/* Status badges */}
            <div className="flex items-center gap-2 flex-wrap">
              {hasBooking && (
                <Badge className="bg-green-100 text-green-700 hover:bg-green-100">
                  Booking
                </Badge>
              )}
              {smsSent && (
                <Badge className="bg-purple-100 text-purple-700 hover:bg-purple-100">
                  <Mail className="h-3 w-3 mr-1" />
                  SMS sent
                </Badge>
              )}
              {forwardedProfile && (
                <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">
                  <PhoneForwarded className="h-3 w-3 mr-1" />
                  Forwarded to {forwardedProfile}
                </Badge>
              )}
            </div>

            {/* Session ID */}
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <span>Session-ID: {call.sessionId}</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleCopySessionId}
                className="h-6 w-6 p-0"
              >
                <Copy className="h-3 w-3" />
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                    <MoreVertical className="h-3 w-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  <DropdownMenuItem onClick={handleReportIssue}>
                    Report issue
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              {copied && (
                <span className="text-xs text-green-600">Copied!</span>
              )}
            </div>
          </div>
        </div>

        {/* Call history header */}
        <div className="px-6 py-3 bg-white border-b border-gray-200">
          <h3 className="text-sm font-medium text-gray-700">Call history</h3>
        </div>

        {/* Scrollable conversation transcript */}
        <div className="flex-1 overflow-y-auto px-6 py-6 bg-gray-50">
          <div className="space-y-3">

            {/* Loading State */}
            {loading && (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-brand-primary" />
                <span className="ml-3 text-sm text-gray-600">Loading conversation...</span>
              </div>
            )}

            {/* Error State */}
            {error && !loading && (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="rounded-full bg-red-50 p-3 mb-3">
                  <X className="h-6 w-6 text-red-600" />
                </div>
                <p className="text-sm font-medium text-gray-900 mb-1">Conversation data not available</p>
                <p className="text-xs text-gray-500">Unable to load the conversation transcript</p>
              </div>
            )}

            {/* Empty State */}
            {!loading && !error && messages.length === 0 && (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="rounded-full bg-gray-100 p-3 mb-3">
                  <Phone className="h-6 w-6 text-gray-400" />
                </div>
                <p className="text-sm font-medium text-gray-900 mb-1">No conversation data</p>
                <p className="text-xs text-gray-500">The conversation transcript is not available for this call</p>
              </div>
            )}

            {/* Messages */}
            {!loading && !error && messages.length > 0 && messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.sender === 'ai' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`relative max-w-[75%] rounded-2xl px-4 py-3 shadow-sm ${message.sender === 'ai'
                      ? 'bg-brand-primary text-white rounded-br-md'
                      : 'bg-gray-800 text-white rounded-bl-md'
                    }`}
                >
                  <p className="text-sm leading-relaxed pr-6">{message.text}</p>
                  {message.sender === 'ai' && message.editable && (
                    <button
                      className="absolute bottom-2 right-2 p-1.5 hover:bg-white/20 rounded-md transition-colors"
                      onClick={() => {
                        // TODO: Open knowledge base editor
                        console.log('Edit message:', message.id)
                      }}
                      title="Edit response in knowledge base"
                    >
                      <Edit className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
